import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Search, Trash2, Download, ChevronUp, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from 'services/api';

interface NewsletterSubscriber {
  id: number;
  email: string;
  subscribed_at: string;
  ip_address?: string;
  country?: string;
  subscription_attempts?: number;
}

const NewsletterSubscribersPage: React.FC = () => {
  const { t } = useTranslation();
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [sortedSubscribers, setSortedSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<string>('subscribed_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  useEffect(() => {
    // Filter subscribers based on search term
    if (!searchTerm) {
      setFilteredSubscribers(subscribers);
    } else {
      const filtered = subscribers.filter(sub => 
        sub.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSubscribers(filtered);
    }
  }, [searchTerm, subscribers]);

  useEffect(() => {
    // Sort and paginate subscribers
    const sorted = [...filteredSubscribers].sort((a, b) => {
      let aValue: any = a[sortField as keyof NewsletterSubscriber];
      let bValue: any = b[sortField as keyof NewsletterSubscriber];
      
      // Handle undefined values
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';
      
      // Handle date fields
      if (sortField === 'subscribed_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // Handle numeric fields
      if (sortField === 'subscription_attempts') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    setSortedSubscribers(sorted);
    setCurrentPage(1); // Reset to first page when sorting changes
  }, [filteredSubscribers, sortField, sortDirection]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedSubscribers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedSubscribers.length / itemsPerPage);

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const fetchSubscribers = async () => {
    try {
      const response = await api.get('/newsletter/');
      setSubscribers(response.data);
      setFilteredSubscribers(response.data);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('newsletter.confirmDelete', 'Are you sure you want to delete this subscriber?'))) {
      try {
        // TEMPORARY: Show alert since delete requires authentication
        alert('Delete functionality temporarily disabled due to authentication issues. Subscriber would be deleted with ID: ' + id);
        // await api.delete(`/newsletter/${id}`);
        // fetchSubscribers(); // Refresh the list
      } catch (error) {
        console.error('Failed to delete subscriber:', error);
      }
    }
  };

  const handleExport = () => {
    // Create CSV content
    const headers = ['Email', 'Subscribed At', 'IP Address', 'Country', 'Tri'];
    const rows = filteredSubscribers.map(sub => [
      sub.email, 
      new Date(sub.subscribed_at).toLocaleString(),
      sub.ip_address || 'N/A',
      sub.country || 'Unknown',
      sub.subscription_attempts || 1
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">{t('newsletter.title', 'Newsletter Subscribers')}</h1>
          <p className="text-gray-600 mt-2">{t('newsletter.description', 'Manage newsletter subscribers')}</p>
        </div>
        <div className="flex space-x-3">
          <Button onClick={handleExport} variant="outline">
            <Download className="mr-2 h-4 w-4" />
            {t('newsletter.export', 'Export Subscribers')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>{t('newsletter.subscribers', 'Subscribers')}</CardTitle>
              <CardDescription>
                {t('newsletter.total', 'Total subscribers: {{count}}', { count: subscribers.length })}
              </CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder={t('newsletter.searchPlaceholder', 'Search email...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">{t('common.loading', 'Loading...')}</div>
          ) : filteredSubscribers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('newsletter.noSubscribers', 'No subscribers yet')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{t('newsletter.email', 'Email')}</span>
                        {sortField === 'email' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('subscribed_at')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{t('newsletter.subscribedAt', 'Subscribed At')}</span>
                        {sortField === 'subscribed_at' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('ip_address')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>IP Address</span>
                        {sortField === 'ip_address' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('country')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Country</span>
                        {sortField === 'country' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('subscription_attempts')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Tri</span>
                        {sortField === 'subscription_attempts' && (
                          sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead className="text-right">{t('newsletter.actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentItems.map((subscriber) => (
                    <TableRow key={subscriber.id}>
                      <TableCell className="font-medium">{subscriber.email}</TableCell>
                      <TableCell>{new Date(subscriber.subscribed_at).toLocaleString()}</TableCell>
                      <TableCell>{subscriber.ip_address || 'N/A'}</TableCell>
                      <TableCell>{subscriber.country || 'Unknown'}</TableCell>
                      <TableCell>{subscriber.ip_address || 'N/A'}</TableCell>
                      <TableCell>{subscriber.country || 'Unknown'}</TableCell>
                      <TableCell>{subscriber.subscription_attempts || 1}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(subscriber.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-500">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, sortedSubscribers.length)} of {sortedSubscribers.length} entries
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterSubscribersPage;